import { useState } from "react";
import {
    Button,
    ListCard,
    ListCardRow,
    Mono,
    Switch,
    Field,
    FieldContent,
    FieldDescription,
    FieldError,
    FieldLabel,
    FieldSet,
    FieldGroup,
    Textarea,
    Input,
    Grid,
    Body,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@athena/ui";
import { Check, Pencil, X } from "lucide-react";
import { useMutation, useQuery } from "@apollo/client/react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { UpdateOrganizationDocument } from "../../graphql/organizations.generated";
import type { OrganizationByIdQuery } from "../../graphql/organizations.generated";
import type { OrganizationStatus } from "@athena/common/graphql/generated/schema";
import { ImageUrlDocument } from "@/graphql/media.generated";
import { ImageUploader } from "@/components/ImageUploader";
import { AutoJoinDomains } from "./AutoJoinDomains";

type Organization = NonNullable<OrganizationByIdQuery["organization"]>;

const STATUS_LABEL: Record<OrganizationStatus, string> = {
    ACTIVE: "Active",
    INACTIVE: "Inactive",
    ARCHIVED: "Archived",
};

const CHARS_500 = 500;
const CHARS_1000 = 1000;

interface OrganizationDetailProps {
    org: Organization;
}

export function OrganizationDetail({ org }: OrganizationDetailProps) {
    const [
        editing,
        setEditing,
    ] = useState<boolean>(false);
    const [
        name,
        setName,
    ] = useState<string>(org.name);
    const [
        status,
        setStatus,
    ] = useState<OrganizationStatus>(org.status);
    const [
        contactPhoneNumber,
        setContactPhoneNumber,
    ] = useState<string>(org.contactPhoneNumber ?? "");
    const [
        contactEmailAddress,
        setContactEmailAddress,
    ] = useState<string>(org.contactEmailAddress ?? "");
    const [
        crisisAlertsEnabled,
        setCrisisAlertsEnabled,
    ] = useState<boolean>(org.crisisAlertsEnabled);
    const [
        emergencyContactEmail,
        setEmergencyContactEmail,
    ] = useState<string>(org.emergencyContactEmail ?? "");
    const [
        emergencyContactPhone,
        setEmergencyContactPhone,
    ] = useState<string>(org.emergencyContactPhone ?? "");
    const [
        chatSettingsEditing,
        setChatSettingsEditing,
    ] = useState<boolean>(false);
    const [
        chatEnabled,
        setChatEnabled,
    ] = useState<boolean>(org.chatEnabled ?? false);
    const [
        chatIntroMessage,
        setChatIntroMessage,
    ] = useState<string>(org.chatIntroMessage ?? "");
    const [
        outOfHoursMessage,
        setOutOfHoursMessage,
    ] = useState<string>(org.outOfHoursMessage ?? "");
    const [
        crisisResponseMessage,
        setCrisisResponseMessage,
    ] = useState<string>(org.crisisResponseMessage ?? "");

    const [
        updateOrganization,
        { loading: saving },
    ] = useMutation(UpdateOrganizationDocument);

    const { data: logoUrlData } = useQuery(ImageUrlDocument, {
        variables: { key: org.logoUrl ?? "" },
        skip: !org.logoUrl,
    });

    const handleLogoUploaded = async (key: string) => {
        try {
            await updateOrganization({
                variables: { id: org.id, input: { logoUrl: key } },
                optimisticResponse: {
                    updateOrganization: {
                        __typename: "Organization",
                        id: org.id,
                        name: org.name,
                        status: org.status,
                        contactPhoneNumber: org.contactPhoneNumber ?? null,
                        contactEmailAddress: org.contactEmailAddress ?? null,
                        crisisAlertsEnabled: org.crisisAlertsEnabled,
                        emergencyContactEmail: org.emergencyContactEmail ?? null,
                        emergencyContactPhone: org.emergencyContactPhone ?? null,
                        logoUrl: key,
                    },
                },
            });
        } catch (error) {
            Sentry.captureException(error);
            toast.error("Couldn't save the logo. Please try again.");
        }
    };

    const handleEdit = () => {
        setName(org.name);
        setStatus(org.status);
        setContactPhoneNumber(org.contactPhoneNumber ?? "");
        setContactEmailAddress(org.contactEmailAddress ?? "");
        setEditing(true);
    };

    const handleCancel = () => {
        setName(org.name);
        setStatus(org.status);
        setContactPhoneNumber(org.contactPhoneNumber ?? "");
        setContactEmailAddress(org.contactEmailAddress ?? "");
        setEditing(false);
    };

    const isDirty =
        name.trim() !== org.name ||
        status !== org.status ||
        contactPhoneNumber.trim() !== (org.contactPhoneNumber ?? "") ||
        contactEmailAddress.trim() !== (org.contactEmailAddress ?? "");

    const handleSave = async () => {
        await updateOrganization({
            variables: {
                id: org.id,
                input: {
                    name: name.trim() || undefined,
                    status,
                    contactPhoneNumber: contactPhoneNumber.trim() || undefined,
                    contactEmailAddress: contactEmailAddress.trim() || undefined,
                },
            },
            optimisticResponse: {
                updateOrganization: {
                    __typename: "Organization",
                    id: org.id,
                    name: name.trim(),
                    status,
                    contactPhoneNumber: contactPhoneNumber.trim() || null,
                    contactEmailAddress: contactEmailAddress.trim() || null,
                    crisisAlertsEnabled: org.crisisAlertsEnabled,
                    emergencyContactEmail: org.emergencyContactEmail ?? null,
                    emergencyContactPhone: org.emergencyContactPhone ?? null,
                    logoUrl: org.logoUrl ?? null,
                },
            },
        });
        setEditing(false);
    };

    const handleChatSettingsEdit = () => {
        setChatIntroMessage(org.chatIntroMessage ?? "");
        setOutOfHoursMessage(org.outOfHoursMessage ?? "");
        setCrisisResponseMessage(org.crisisResponseMessage ?? "");
        setChatEnabled(org.chatEnabled ?? false);
        setCrisisAlertsEnabled(org.crisisAlertsEnabled);
        setEmergencyContactEmail(org.emergencyContactEmail ?? "");
        setEmergencyContactPhone(org.emergencyContactPhone ?? "");
        setChatSettingsEditing(true);
    };

    const handleChatSettingsCancel = () => {
        setChatIntroMessage(org.chatIntroMessage ?? "");
        setOutOfHoursMessage(org.outOfHoursMessage ?? "");
        setCrisisResponseMessage(org.crisisResponseMessage ?? "");
        setChatEnabled(org.chatEnabled ?? false);
        setCrisisAlertsEnabled(org.crisisAlertsEnabled);
        setEmergencyContactEmail(org.emergencyContactEmail ?? "");
        setEmergencyContactPhone(org.emergencyContactPhone ?? "");
        setChatSettingsEditing(false);
    };

    const isChatSettingsDirty =
        chatIntroMessage.trim() !== (org.chatIntroMessage ?? "") ||
        outOfHoursMessage.trim() !== (org.outOfHoursMessage ?? "") ||
        crisisResponseMessage.trim() !== (org.crisisResponseMessage ?? "") ||
        chatEnabled !== (org.chatEnabled ?? false) ||
        crisisAlertsEnabled !== org.crisisAlertsEnabled ||
        emergencyContactEmail.trim() !== (org.emergencyContactEmail ?? "") ||
        emergencyContactPhone.trim() !== (org.emergencyContactPhone ?? "");

    /**
     * An organization taking crisis alerts needs somewhere to send them. One contact is enough —
     * partners rarely want both — and with alerts off neither is required.
     *
     * Only the email is actually delivered to today (nothing dials the phone), which the field's
     * own description says. It is not enforced here because that is a gap in what we have built,
     * not a rule about what a partner may choose.
     */
    const crisisContactMissing = crisisAlertsEnabled && !emergencyContactEmail.trim() && !emergencyContactPhone.trim();

    const handleChatSettingsSave = async () => {
        await updateOrganization({
            variables: {
                id: org.id,
                input: {
                    chatEnabled,
                    chatIntroMessage: chatIntroMessage.trim() || null,
                    outOfHoursMessage: outOfHoursMessage.trim() || null,
                    crisisResponseMessage: crisisResponseMessage.trim() || null,
                    crisisAlertsEnabled,
                    emergencyContactEmail: emergencyContactEmail.trim() || null,
                    emergencyContactPhone: emergencyContactPhone.trim() || null,
                },
            },
            optimisticResponse: {
                updateOrganization: {
                    __typename: "Organization",
                    id: org.id,
                    name: org.name,
                    status: org.status,
                    contactPhoneNumber: org.contactPhoneNumber ?? null,
                    contactEmailAddress: org.contactEmailAddress ?? null,
                    crisisAlertsEnabled,
                    emergencyContactEmail: emergencyContactEmail.trim() || null,
                    emergencyContactPhone: emergencyContactPhone.trim() || null,
                    chatEnabled,
                    chatIntroMessage: chatIntroMessage.trim() || null,
                    outOfHoursMessage: outOfHoursMessage.trim() || null,
                    crisisResponseMessage: crisisResponseMessage.trim() || null,
                    logoUrl: org.logoUrl ?? null,
                },
            },
        });
        setChatSettingsEditing(false);
    };

    return (
        <Grid cols={2}>
            <div className="flex flex-col gap-4">
                <ListCard
                    title="Basic Information"
                    description="Essential information about the organization"
                    action={
                        <div className="flex flex-row gap-2">
                            {editing ? (
                                <>
                                    <Button
                                        variant={"outline"}
                                        title="Save"
                                        aria-label="Save"
                                        disabled={!name.trim() || !isDirty || saving}
                                        onClick={handleSave}
                                    >
                                        <Check className="text-muted-foreground h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant={"outline"}
                                        title="Cancel"
                                        aria-label="Cancel"
                                        onClick={handleCancel}
                                        disabled={saving}
                                    >
                                        <X className="text-muted-foreground h-4 w-4" />
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    variant={"outline"}
                                    title="Edit basic information"
                                    aria-label="Edit basic information"
                                    onClick={handleEdit}
                                >
                                    <Pencil className="text-muted-foreground h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    }
                >
                    <ListCardRow>
                        <FieldSet className="w-full max-w-none">
                            <FieldGroup>
                                <Field>
                                    <FieldLabel htmlFor="org-name">Organization Name</FieldLabel>
                                    <Input
                                        required
                                        id="org-name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        disabled={!editing}
                                        type="text"
                                    />
                                </Field>
                                <Field>
                                    <FieldLabel htmlFor="org-status">Status</FieldLabel>
                                    <Select
                                        value={status}
                                        onValueChange={(value) => setStatus(value as OrganizationStatus)}
                                        disabled={!editing}
                                    >
                                        <SelectTrigger id="org-status" className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(STATUS_LABEL).map(
                                                ([
                                                    value,
                                                    label,
                                                ]) => (
                                                    <SelectItem key={value} value={value}>
                                                        {label}
                                                    </SelectItem>
                                                ),
                                            )}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field>
                                    <FieldLabel htmlFor="contact-email">Contact Email</FieldLabel>
                                    <Input
                                        id="contact-email"
                                        type="email"
                                        value={contactEmailAddress}
                                        onChange={(e) => setContactEmailAddress(e.target.value)}
                                        placeholder="email@example.com"
                                        disabled={!editing}
                                    />
                                </Field>
                                <Field>
                                    <FieldLabel htmlFor="contact-phone">Contact Phone Number</FieldLabel>
                                    <Input
                                        id="contact-phone"
                                        type="text"
                                        value={contactPhoneNumber}
                                        onChange={(e) => setContactPhoneNumber(e.target.value)}
                                        placeholder="(555) 555-5555"
                                        disabled={!editing}
                                    />
                                </Field>
                                <Field>
                                    <FieldLabel htmlFor="org-licenses">Licenses</FieldLabel>
                                    <FieldDescription className="text-text-body text-xs">
                                        {org.licenseUsage.total == null
                                            ? "Uncapped — this organization has no license limit."
                                            : "Seats bought and how many are currently assigned to members."}
                                    </FieldDescription>
                                    <Body id="org-licenses" className="text-text-body font-mono text-sm">
                                        {org.licenseUsage.total == null
                                            ? `${org.licenseUsage.used} used · Unlimited`
                                            : `${org.licenseUsage.used} of ${org.licenseUsage.total} used · ${org.licenseUsage.available} available`}
                                    </Body>
                                </Field>

                                {/*
                                 * The uploader is the whole field now. This used to be a hand-rolled
                                 * dashed well wrapping a bare button — which meant two nested wells
                                 * once the uploader grew its own, a <label> wrapping buttons, and a
                                 * htmlFor pointing at an id that was never rendered.
                                 */}
                                <Field>
                                    <FieldLabel>Organization logo</FieldLabel>
                                    <ImageUploader
                                        organizationId={org.id}
                                        onUploaded={handleLogoUploaded}
                                        label={org.logoUrl ? "Replace logo" : "Upload logo"}
                                        currentUrl={logoUrlData?.imageUrl ?? null}
                                        hint="Shown to members. PNG, JPEG, WebP, or GIF, up to 5 MB. Or drag one here."
                                    />
                                </Field>
                            </FieldGroup>
                        </FieldSet>
                    </ListCardRow>
                </ListCard>
                <AutoJoinDomains org={org} />
            </div>

            <div className="flex flex-col gap-4">
                <ListCard
                    title="Chat Settings"
                    description="Configure chat functionality for the organization"
                    action={
                        <div className="flex flex-row gap-2">
                            {chatSettingsEditing ? (
                                <>
                                    <Button
                                        variant={"outline"}
                                        title="Save"
                                        aria-label="Save"
                                        disabled={!isChatSettingsDirty || crisisContactMissing || saving}
                                        onClick={handleChatSettingsSave}
                                    >
                                        <Check className="text-muted-foreground h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant={"outline"}
                                        title="Cancel"
                                        aria-label="Cancel"
                                        onClick={handleChatSettingsCancel}
                                        disabled={saving}
                                    >
                                        <X className="text-muted-foreground h-4 w-4" />
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    variant={"outline"}
                                    title="Edit chat settings"
                                    aria-label="Edit chat settings"
                                    onClick={handleChatSettingsEdit}
                                >
                                    <Pencil className="text-muted-foreground h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    }
                >
                    <ListCardRow>
                        <Field orientation="horizontal" className="max-w-sm">
                            <Switch
                                id="enable-chat"
                                checked={chatEnabled}
                                onCheckedChange={setChatEnabled}
                                disabled={!chatSettingsEditing}
                            />
                            <FieldContent>
                                <FieldLabel htmlFor="enable-chat">Enable Chat</FieldLabel>
                                <FieldDescription className="text-text-body text-xs">
                                    Allow members of this organization to use the coaching chat feature.
                                </FieldDescription>
                            </FieldContent>
                        </Field>
                    </ListCardRow>
                    <ListCardRow>
                        <FieldSet className="w-full max-w-none">
                            <Field>
                                <FieldLabel htmlFor="custom-chat-intro">Custom Chat Intro Message</FieldLabel>
                                <FieldDescription className="text-text-body text-xs">
                                    Sent as the first message when a user starts a new chat. Falls back to a platform
                                    default when unset.
                                </FieldDescription>
                                <Textarea
                                    id="custom-chat-intro"
                                    placeholder="Enter a custom welcome message for users starting a chat..."
                                    rows={4}
                                    maxLength={CHARS_500}
                                    value={chatIntroMessage}
                                    onChange={(e) => setChatIntroMessage(e.target.value)}
                                    disabled={!chatSettingsEditing}
                                />
                                <Mono size="xs">
                                    {chatIntroMessage.length}/{CHARS_500} characters
                                </Mono>
                            </Field>
                        </FieldSet>
                    </ListCardRow>
                    <ListCardRow>
                        <FieldSet className="w-full max-w-none">
                            <Field>
                                <FieldLabel htmlFor="out-of-hours-message">Out-of-Hours Message</FieldLabel>
                                <FieldDescription className="text-text-body text-xs">
                                    Sent once per out-of-hours session when a user messages outside business hours
                                    (Mon–Fri, 10am–8pm ET). Falls back to a platform default when unset.
                                </FieldDescription>
                                <Textarea
                                    id="out-of-hours-message"
                                    placeholder="Enter a custom out-of-hours auto-response..."
                                    rows={4}
                                    maxLength={CHARS_500}
                                    value={outOfHoursMessage}
                                    onChange={(e) => setOutOfHoursMessage(e.target.value)}
                                    disabled={!chatSettingsEditing}
                                />
                                <Mono size="xs">
                                    {outOfHoursMessage.length}/{CHARS_500} characters
                                </Mono>
                            </Field>
                        </FieldSet>
                    </ListCardRow>
                    <ListCardRow>
                        <Field>
                            <FieldLabel htmlFor="crisis-response-message">Crisis Response Message</FieldLabel>
                            <FieldDescription className="text-text-body text-xs">
                                Sent automatically when a message trips a crisis keyword. Name your own hotline here —
                                unset, the platform default is used instead, and a partner should not have to inherit
                                ours on the most sensitive message we send. Once per conversation within the cooldown
                                window, not per message.
                            </FieldDescription>
                            <Textarea
                                id="crisis-response-message"
                                placeholder="Enter a custom crisis auto-response..."
                                rows={4}
                                maxLength={CHARS_1000}
                                value={crisisResponseMessage}
                                onChange={(e) => setCrisisResponseMessage(e.target.value)}
                                disabled={!chatSettingsEditing}
                            />
                            <Mono size="xs">
                                {crisisResponseMessage.length}/{CHARS_1000} characters
                            </Mono>
                        </Field>
                    </ListCardRow>
                    <ListCardRow>
                        <Field orientation="horizontal" className="max-w-sm">
                            <Switch
                                id="enable-crisis-alerts"
                                checked={crisisAlertsEnabled}
                                onCheckedChange={setCrisisAlertsEnabled}
                                disabled={!chatSettingsEditing}
                            />
                            <FieldContent>
                                <FieldLabel htmlFor="enable-crisis-alerts">Crisis Alerts</FieldLabel>
                                <FieldDescription className="text-text-body text-xs">
                                    Notify this organization when a member&rsquo;s message trips a crisis keyword. Turn
                                    it off for a partner who does not want to be involved in crisis triggers — members
                                    here still receive the crisis response message, and our own team is still alerted.
                                </FieldDescription>
                            </FieldContent>
                        </Field>
                    </ListCardRow>
                    <ListCardRow className="flex flex-col">
                        <Field>
                            <FieldLabel htmlFor="crisis-email">Crisis Alert Email</FieldLabel>
                            <FieldDescription className="text-text-body text-xs">
                                This is the email address that will get notified when a Crisis Alert is triggered.
                                {crisisAlertsEnabled
                                    ? " It is the only contact an alert is actually delivered to today."
                                    : " Not used while Crisis Alerts are off."}
                            </FieldDescription>
                            <Input
                                id="crisis-email"
                                type="email"
                                value={emergencyContactEmail}
                                onChange={(e) => setEmergencyContactEmail(e.target.value)}
                                placeholder="email@example.com"
                                disabled={!chatSettingsEditing || !crisisAlertsEnabled}
                            />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="crisis-phone">Crisis Alert Phone Number</FieldLabel>
                            <FieldDescription className="text-text-body text-xs">
                                Kept on file as a crisis contact. Nothing dials it yet, so an organization with only a
                                phone number here is not notified in practice.
                            </FieldDescription>
                            <Input
                                id="crisis-phone"
                                type="text"
                                value={emergencyContactPhone}
                                onChange={(e) => setEmergencyContactPhone(e.target.value)}
                                placeholder="(555) 555-5555"
                                disabled={!chatSettingsEditing || !crisisAlertsEnabled}
                            />
                        </Field>
                        {crisisContactMissing && (
                            <FieldError>
                                Add a crisis alert email or phone number, or turn Crisis Alerts off.
                            </FieldError>
                        )}
                    </ListCardRow>
                </ListCard>
            </div>
        </Grid>
    );
}
